import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Pencil,
    Trash2,
    PlusCircle,
    Settings,
    Play,
    Pause,
    Eye,
    Copy,
    Target,
    Hash,
    Search,
} from 'lucide-react';
import intentDefinitionService from './intentDefinitionService';
import {
    INTENT_GROUP_LABELS,
    INTENT_GROUP_ORDER,
    resolveIntentGroupFromListFields,
    type IntentDefinitionListItem,
    type IntentGroupKey,
} from './intentDefinitionPayload';

// Type definitions
export type Theme = 'dark' | 'light';

export interface ThemeColors {
    primary: string;
    secondary: string;
    background: string;
    surface: string;
    border: string;
    text: {
        primary: string;
        secondary: string;
        muted: string;
    };
    accent: string;
    hover: string;
    input: string;
}

interface Intent {
    id: string;
    name: string;
    code: string;
    status: 'active' | 'inactive';
    confidence: number;
    updatedAt: string;
    group: IntentGroupKey;
}

type GroupFilter = 'all' | IntentGroupKey;

function mapIntentListItem(record: IntentDefinitionListItem): Intent {
    const status = String(record.status ?? '').trim().toLowerCase();
    return {
        id: record._id,
        name: record.name ?? '',
        code: record.code ?? '',
        status: status === 'inactive' ? 'inactive' : 'active',
        confidence: Number(record.confidence_threshold ?? 0),
        updatedAt: record.updated_at ?? new Date().toISOString(),
        group: resolveIntentGroupFromListFields(record.intent_type, record.report_type),
    };
}

export interface ChatbotIntentsListingProps {
    theme?: Theme;
}

// Theme Configuration
const THEME_CONFIG: Record<Theme, ThemeColors> = {
    dark: {
        primary: 'bg-neutral-900',
        secondary: 'bg-neutral-800/80',
        background: 'bg-neutral-950',
        surface: 'bg-neutral-900',
        border: 'border-neutral-800',
        text: {
            primary: 'text-neutral-100',
            secondary: 'text-neutral-300',
            muted: 'text-neutral-500',
        },
        accent: 'bg-indigo-600',
        hover: 'hover:bg-neutral-800/80',
        input: 'bg-neutral-900 border-neutral-700 text-neutral-100 placeholder-neutral-500',
    },
    light: {
        primary: 'bg-white',
        secondary: 'bg-slate-50',
        background: 'bg-slate-100',
        surface: 'bg-white',
        border: 'border-slate-200',
        text: {
            primary: 'text-slate-900',
            secondary: 'text-slate-800',
            muted: 'text-slate-700',
        },
        accent: 'bg-indigo-600',
        hover: 'hover:bg-slate-50',
        input: 'bg-white border-slate-300 text-slate-900 placeholder-slate-400',
    },
};

const ChatbotIntentsListing: React.FC<ChatbotIntentsListingProps> = ({
    theme: themeProp = 'dark'
}) => {
    const navigate = useNavigate();
    const { chatbotId } = useParams<{ chatbotId?: string }>();
    const [theme, setTheme] = useState<Theme>(themeProp);
    const colors = THEME_CONFIG[theme];

    useEffect(() => {
        const activeTheme = document.documentElement.getAttribute('data-theme');
        setTheme(activeTheme === 'white' ? 'light' : 'dark');
    }, []);
    const [intents, setIntents] = useState<Intent[]>([]);
    const [loadingIntents, setLoadingIntents] = useState(false);
    const [intentsError, setIntentsError] = useState<string | null>(null);
    const [deletingIntentId, setDeletingIntentId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');

    const fetchIntents = useCallback(async () => {
        if (!chatbotId) {
            setIntents([]);
            return;
        }

        setLoadingIntents(true);
        setIntentsError(null);
        try {
            const records = await intentDefinitionService.listIntentDefinitions(chatbotId);
            setIntents(records.map(mapIntentListItem));
        } catch (error) {
            setIntents([]);
            setIntentsError(error instanceof Error ? error.message : 'Failed to load intents');
        } finally {
            setLoadingIntents(false);
        }
    }, [chatbotId]);

    useEffect(() => {
        void fetchIntents();
    }, [fetchIntents]);

    const filteredIntents = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return intents.filter((intent) => {
            if (groupFilter !== 'all' && intent.group !== groupFilter) {
                return false;
            }
            if (!query) return true;
            return (
                intent.name.toLowerCase().includes(query) ||
                intent.code.toLowerCase().includes(query)
            );
        });
    }, [intents, searchQuery, groupFilter]);

    const groupedSections = useMemo(() => {
        const byGroup = new Map<IntentGroupKey, Intent[]>();
        for (const intent of filteredIntents) {
            const list = byGroup.get(intent.group) ?? [];
            list.push(intent);
            byGroup.set(intent.group, list);
        }

        const sections: { key: IntentGroupKey; label: string; intents: Intent[] }[] = [];

        for (const key of INTENT_GROUP_ORDER) {
            const list = byGroup.get(key);
            if (!list?.length) {
                // Show empty section only when that group is explicitly selected
                if (groupFilter === key) {
                    sections.push({ key, label: INTENT_GROUP_LABELS[key], intents: [] });
                }
                continue;
            }
            sections.push({ key, label: INTENT_GROUP_LABELS[key], intents: list });
        }

        const other = byGroup.get('other');
        if (other?.length || groupFilter === 'other') {
            sections.push({
                key: 'other',
                label: INTENT_GROUP_LABELS.other,
                intents: other ?? [],
            });
        }

        return sections;
    }, [filteredIntents, groupFilter]);

    const intentsBasePath = chatbotId
        ? `/portal/chatbot/${chatbotId}/intents`
        : '/portal/createchatagent/intents';

    const openIntentDefinition = (mode: 'create' | 'edit', intentId?: string): void => {
        if (mode === 'create') {
            navigate(`${intentsBasePath}/new`);
            return;
        }
        if (intentId) {
            navigate(`${intentsBasePath}/${intentId}/edit`);
        }
    };

    const toggleIntentStatus = (id: string): void => {
        setIntents(prev => prev.map(intent =>
            intent.id === id
                ? { ...intent, status: intent.status === 'active' ? 'inactive' : 'active' }
                : intent
        ));
    };

    const handleDeleteIntent = async (intent: Intent): Promise<void> => {
        if (
            !window.confirm(
                `Are you sure you want to delete "${intent.name}"? This action cannot be undone.`
            )
        ) {
            return;
        }
        if (deletingIntentId) return;

        setDeletingIntentId(intent.id);
        setIntentsError(null);
        try {
            await intentDefinitionService.deleteIntentDefinition(intent.id);
            setIntents((prev) => prev.filter((item) => item.id !== intent.id));
        } catch (error) {
            setIntentsError(
                error instanceof Error ? error.message : 'Failed to delete intent'
            );
        } finally {
            setDeletingIntentId(null);
        }
    };

    const formatConfidence = (confidence: number): string => {
        return `${(confidence * 100).toFixed(1)}%`;
    };

    const groupFilterOptions: { value: GroupFilter; label: string }[] = [
        { value: 'all', label: 'All groups' },
        ...INTENT_GROUP_ORDER.map((key) => ({
            value: key as GroupFilter,
            label: INTENT_GROUP_LABELS[key],
        })),
    ];

    const renderIntentRow = (intent: Intent) => (
        <tr key={intent.id} className={`border-t ${colors.border} ${colors.hover} transition-colors`}>
            <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200'}`}>
                        <Target className={`w-4 h-4 ${theme === 'dark' ? 'text-slate-200' : 'text-slate-700'}`} />
                    </div>
                    <span className={`font-medium ${colors.text.primary}`}>{intent.name}</span>
                </div>
            </td>
            <td className="px-4 py-3">
                <span className={`font-mono text-xs ${colors.text.secondary}`}>{intent.code}</span>
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                    <div className={`w-16 ${theme === 'dark' ? 'bg-neutral-800' : 'bg-slate-200'} rounded-full h-2`}>
                        <div
                            className={`h-2 rounded-full transition-all ${
                                intent.confidence >= 0.9
                                    ? theme === 'dark' ? 'bg-slate-400' : 'bg-slate-600'
                                    : intent.confidence >= 0.8
                                      ? theme === 'dark' ? 'bg-slate-500' : 'bg-slate-500'
                                      : theme === 'dark' ? 'bg-slate-600' : 'bg-slate-400'
                            }`}
                            style={{ width: `${intent.confidence * 100}%` }}
                        ></div>
                    </div>
                    <span className={`text-xs font-mono ${colors.text.muted}`}>{formatConfidence(intent.confidence)}</span>
                </div>
            </td>
            <td className="px-4 py-3">
                <span className={`font-mono text-xs ${colors.text.muted}`}>
                    {new Date(intent.updatedAt).toLocaleString()}
                </span>
            </td>
            <td className="px-4 py-3">
                <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${
                        intent.status === 'active'
                            ? theme === 'dark'
                                ? 'bg-slate-800 text-slate-200 border-slate-600'
                                : 'bg-slate-100 text-slate-800 border-slate-300'
                            : theme === 'dark'
                              ? 'bg-neutral-800/80 text-neutral-400 border-neutral-700'
                              : 'bg-slate-50 text-slate-500 border-slate-200'
                    }`}
                >
                    {intent.status === 'active' ? (
                        <Play className="w-3 h-3" />
                    ) : (
                        <Pause className="w-3 h-3" />
                    )}
                    {intent.status}
                </span>
            </td>
            <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                    <button
                        onClick={() => toggleIntentStatus(intent.id)}
                        className={`p-1 rounded transition-colors ${colors.text.muted} ${colors.hover}`}
                        title={intent.status === 'active' ? 'Deactivate' : 'Activate'}
                    >
                        {intent.status === 'active' ? (
                            <Pause className="w-4 h-4" />
                        ) : (
                            <Play className="w-4 h-4" />
                        )}
                    </button>
                    <button
                        onClick={() => openIntentDefinition('edit', intent.id)}
                        className={`p-1 rounded transition-colors ${colors.text.muted} ${colors.hover}`}
                        title="View Details"
                    >
                        <Eye className="w-4 h-4" />
                    </button>
                    <button className={`p-1 rounded transition-colors ${colors.text.muted} ${colors.hover}`} title="Copy Intent ID">
                        <Copy className="w-4 h-4" />
                    </button>
                    <button className={`p-1 rounded transition-colors ${colors.text.muted} ${colors.hover}`} title="Train Intent">
                        <Settings className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => openIntentDefinition('edit', intent.id)}
                        className={`p-1 rounded transition-colors ${colors.text.muted} ${colors.hover}`}
                        title="Edit"
                    >
                        <Pencil className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => void handleDeleteIntent(intent)}
                        disabled={deletingIntentId === intent.id}
                        className={`p-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${theme === 'dark' ? 'text-rose-400 hover:text-rose-300 hover:bg-rose-950/30' : 'text-rose-600 hover:text-rose-700 hover:bg-rose-50'}`}
                        title={deletingIntentId === intent.id ? 'Deleting...' : 'Delete'}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </td>
        </tr>
    );

    return (
        <div className={`h-full ${colors.background} overflow-auto`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b ${colors.border} ${colors.primary} sticky top-0 z-10`}>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className={`text-lg font-semibold ${colors.text.primary}`}>Chatbot Intents</h1>
                        <p className={`text-sm ${colors.text.muted} mt-1`}>
                            Manage chatbot intents, training phrases, and response patterns for natural language understanding.
                        </p>
                    </div>

                    <button
                        onClick={() => openIntentDefinition('create')}
                        className={`px-4 py-2 ${colors.accent} hover:bg-indigo-700 text-white text-sm rounded-md transition-colors flex items-center gap-2 font-medium`}
                    >
                        <PlusCircle className="w-4 h-4" /> Add New Intent
                    </button>
                </div>

                {/* Filters */}
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${colors.text.muted}`} />
                        <input
                            type="search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by name or code…"
                            className={`w-full pl-9 pr-3 py-2 text-sm rounded-md border ${colors.input} focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                            aria-label="Search intents"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        {groupFilterOptions.map((option) => {
                            const isActive = groupFilter === option.value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => setGroupFilter(option.value)}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                        isActive
                                            ? theme === 'dark'
                                                ? 'bg-indigo-600 text-white border-indigo-500'
                                                : 'bg-indigo-600 text-white border-indigo-600'
                                            : `${colors.surface} ${colors.border} ${colors.text.secondary} ${colors.hover}`
                                    }`}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-6 max-w-7xl mx-auto">
                {!chatbotId && (
                    <p className={`text-sm ${colors.text.muted} mb-4`}>
                        Open intents from a specific chatbot to load saved intent definitions.
                    </p>
                )}
                {intentsError && (
                    <p className="text-sm text-rose-500 mb-4">{intentsError}</p>
                )}
                {/* Stats Cards — filtered set */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className={`${colors.surface} border ${colors.border} rounded-lg p-4`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className={`text-sm ${colors.text.muted}`}>Total Intents</p>
                                <p className={`text-2xl font-semibold ${colors.text.primary}`}>{filteredIntents.length}</p>
                            </div>
                            <Target className={`w-8 h-8 ${colors.text.muted}`} />
                        </div>
                    </div>

                    <div className={`${colors.surface} border ${colors.border} rounded-lg p-4`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className={`text-sm ${colors.text.muted}`}>Active Intents</p>
                                <p className={`text-2xl font-semibold ${colors.text.primary}`}>
                                    {filteredIntents.filter(i => i.status === 'active').length}
                                </p>
                            </div>
                            <Play className={`w-8 h-8 ${colors.text.muted}`} />
                        </div>
                    </div>

                    <div className={`${colors.surface} border ${colors.border} rounded-lg p-4`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className={`text-sm ${colors.text.muted}`}>Avg Confidence</p>
                                <p className={`text-2xl font-semibold ${colors.text.primary}`}>
                                    {formatConfidence(filteredIntents.reduce((sum, intent) => sum + intent.confidence, 0) / filteredIntents.length || 0)}
                                </p>
                            </div>
                            <Hash className={`w-8 h-8 ${colors.text.muted}`} />
                        </div>
                    </div>
                </div>

                {loadingIntents ? (
                    <div className={`overflow-x-auto border ${colors.border} rounded-lg ${colors.surface}`}>
                        <p className={`px-4 py-8 text-center text-sm ${colors.text.muted}`}>Loading intents...</p>
                    </div>
                ) : filteredIntents.length === 0 ? (
                    <div className={`overflow-x-auto border ${colors.border} rounded-lg ${colors.surface}`}>
                        <p className={`px-4 py-8 text-center text-sm ${colors.text.muted}`}>
                            {!chatbotId
                                ? 'No chatbot selected.'
                                : intents.length === 0
                                  ? 'No intents found for this chatbot.'
                                  : 'No intents match the current filters.'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {groupedSections.map((section) => (
                            <div key={section.key} className={`overflow-x-auto border ${colors.border} rounded-lg ${colors.surface}`}>
                                <div className={`px-4 py-3 border-b ${colors.border} ${colors.primary} flex items-center justify-between`}>
                                    <h2 className={`text-sm font-semibold ${colors.text.primary}`}>
                                        {section.label}
                                    </h2>
                                    <span className={`text-xs font-mono ${colors.text.muted}`}>
                                        {section.intents.length}
                                    </span>
                                </div>
                                <table className={`min-w-full text-sm text-left ${colors.text.secondary}`}>
                                    <thead className={`${colors.primary} ${colors.text.muted} border-b ${colors.border}`}>
                                        <tr>
                                            <th className="px-4 py-3 font-medium">Intent Name</th>
                                            <th className="px-4 py-3 font-medium">Code</th>
                                            <th className="px-4 py-3 font-medium">Confidence</th>
                                            <th className="px-4 py-3 font-medium">Updated</th>
                                            <th className="px-4 py-3 font-medium">Status</th>
                                            <th className="px-4 py-3 text-right font-medium">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={colors.surface}>
                                        {section.intents.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className={`px-4 py-6 text-center ${colors.text.muted}`}>
                                                    No intents in this group.
                                                </td>
                                            </tr>
                                        ) : (
                                            section.intents.map(renderIntentRow)
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>
                )}
            </div>

        </div>
    );
};

export default ChatbotIntentsListing;
